// src/api/geminiApi.ts
import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from "@google/generative-ai";
import { RedditComment, RedditMoreComments } from '../types/reddit';

// --- System Prompt ---
const SYSTEM_PROMPT = `You are an expert summarizer tasked with creating a concise "TL;DR" (Too Long; Didn't Read) for a Reddit post and its comments.

Your goal is to provide a quick, insightful overview suitable for someone who doesn't have time to read everything.

Instructions:
1.  **Analyze the Post:** If the post has text content (provided below), create a very brief (1-2 sentence) TL;DR summarizing its main point, question, or story.
2.  **Analyze Comments:** Review the provided comments. Identify the main themes, common reactions, prevailing sentiment, or interesting discussion points.
3.  **Synthesize:** Combine the post TL;DR (if applicable) and the comment insights into a single, coherent summary.
4.  **Non-Text Posts:** If the post content is marked as "[Non-text post content]", focus *exclusively* on summarizing the comments and their reactions to the post (which might be an image, video, link, etc.). Do *not* attempt to describe the non-text content itself.
5.  **Format:** Structure your response clearly. You can use markdown for simple formatting like bolding section titles (e.g., "**TL;DR:**", "**Comments:**").
6.  **Conciseness:** Keep the entire summary brief and easy to digest. Aim for 3-5 sentences total.
7.  **Tone:** Maintain a neutral and informative tone.
8.  **Insight:** Go beyond just listing topics; provide a small insight into *how* people are reacting or what the general consensus seems to be in the comments.

You will be given the post's text content (or an indicator for non-text posts) and a selection of comments. Base your summary *only* on the provided information.
`;

// --- Helper to format comments ---
// Selects top-level or highly voted comments and formats them simply. Limits total length.
const formatCommentsForPrompt = (
    comments: (RedditComment | RedditMoreComments)[],
    maxChars: number = 4000 // Limit comment text length
): string => {
    let content = "";
    let charCount = 0;

    // Simple approach: Take the first few top-level comments
    const topLevelComments = comments.filter(c => c.kind === 't1' && c.data.depth === 0) as RedditComment[];

    // Sort by score descending to prioritize popular comments if needed
    // topLevelComments.sort((a, b) => b.data.score - a.data.score);

    for (const comment of topLevelComments) {
        if (comment.data.body && comment.data.body !== '[deleted]' && comment.data.body !== '[removed]') {
            const commentText = `Comment by u/${comment.data.author} (${comment.data.score} points):\n${comment.data.body}\n\n---\n\n`;
            if (charCount + commentText.length <= maxChars) {
                content += commentText;
                charCount += commentText.length;
            } else {
                // Add partial comment if possible, or just stop
                const remainingChars = maxChars - charCount;
                if (remainingChars > 100) { // Only add if significant space left
                    content += commentText.substring(0, remainingChars) + "...\n\n---\n\n";
                }
                break; // Stop adding comments once limit is reached
            }
        }
        // Limit the number of comments processed to avoid overly long prompts regardless of char count
        if (content.split('\n---\n').length > 15) break;
    }

    return content.trim() || "No comments provided or available.";
};


// --- Main Generation Function ---
export const generateTldr = async (
    apiKey: string,
    postTitle: string,
    postSelfText: string | null | undefined, // Can be null/empty for non-text posts
    comments: (RedditComment | RedditMoreComments)[]
): Promise<string> => {
    if (!apiKey) {
        throw new Error("Gemini API Key is missing.");
    }

    const generationConfig = {
        temperature: 0, 
        topK: 1,
        topP: 1,
        maxOutputTokens: 2048, 
    };

     const safetySettings = [
        { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
        { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
        { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
        { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
    ];

    const genAI = new GoogleGenerativeAI(apiKey);
    const model = genAI.getGenerativeModel({
        model: "gemini-2.0-flash-lite", 
        systemInstruction: SYSTEM_PROMPT,
        generationConfig: generationConfig,
        safetySettings: safetySettings
    });



    const formattedComments = formatCommentsForPrompt(comments);
    const postContentForPrompt = (postSelfText && postSelfText.trim()) ? postSelfText.trim() : "[Non-text post content]";

    const prompt = `
Reddit Post Title: ${postTitle}

Post Content:
${postContentForPrompt}

---

Selected Comments:
${formattedComments}
`;

    try {
        const result = await model.generateContent(prompt); // Pass config and safety if needed directly here in future SDK versions if supported
        // For current SDK version, config/safety are part of model initialization or separate params
        // const result = await model.generateContent({
        //     contents: [{ role: "user", parts: [{ text: prompt }] }],
        //     generationConfig,
        //     safetySettings,
        // });


        // Check for safety blocks or empty response
        if (!result.response) {
             throw new Error("Gemini API returned no response.");
        }

        const responseText = result.response.text();

        if (!responseText) {
             // Attempt to get candidate details if available
             const candidates = result.response.candidates;
             if (candidates && candidates.length > 0 && candidates[0]!.finishReason !== 'STOP') {
                 throw new Error(`Generation stopped: ${candidates[0]!.finishReason}. ${candidates[0]!.safetyRatings?.map(r => `${r.category}: ${r.probability}`).join(', ') || ''}`);
             }
             throw new Error("Gemini API returned an empty response text.");
        }

        return responseText;

    } catch (error: any) {
        console.error("Error calling Gemini API:", error);
        // Provide more specific error messages
        if (error.message.includes("API key not valid")) {
             throw new Error("Invalid Gemini API Key. Please check your key in Settings.");
        }
        if (error.message.includes("quota")) {
             throw new Error("Gemini API quota exceeded. Please check your usage limits.");
        }
        // Handle potential safety blocks if error structure reveals it
        if (error.message.includes("stopped: SAFETY")) {
             throw new Error("Generation blocked due to safety settings.");
        }
        throw new Error(`Failed to generate TL;DR: ${error.message}`);
    }
};